import clsx from 'clsx'
import { unsafeCSS } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { html, unsafeStatic } from 'lit/static-html.js'
import { kebabCase } from 'lodash-es'

import { anchorClass } from '@/common/const'
import { adoptLightStyles } from '@/internal/adopt-light-styles'
import { DdsElement } from '@/internal/dds-hu-element'

import typographyStyles from '../typography/_styles/typography.scss?inline'
import { TypographyVariant } from '../typography/typography.component'
import styles from './anchor-heading.scss?inline'

export type HeadingVariant = Extract<
  TypographyVariant,
  'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
>

/**
 * `dap-ds-anchor-heading`
 * @summary Anchor heading is a heading with an anchor link.
 * @element dap-ds-anchor-heading
 * @title - Anchor heading
 *
 * @attribute {'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'} variant - The variant of the heading. Default is `h2`. Can be `h1`, `h2`, `h3`, `h4`, `h5`, or `h6`.
 * @attribute {string} label - The label of the heading.
 *
 * @csspart base - The main anchor heading container.
 * @csspart link - The link of the anchor heading. dap-ds-link element.
 * @csspart link-base - The base of the link part.
 * @csspart text - The text of the anchor heading.
 *
 * @cssproperty --dds-anchor-link-color - The color of the anchor link. Default is the primary color.
 * @cssproperty --dds-anchor-link-opacity - The opacity of the anchor link when not hovered. Default is 0.
 * @cssproperty --dds-anchor-link-hover-opacity - The opacity of the anchor link when hovered. Default is 1.
 * @cssproperty --dds-anchor-link-transition - The transition property for the anchor link opacity. Default is 'opacity 0.2s ease-in-out'.
 */
@customElement('dap-ds-anchor-heading')
export default class DapDSAnchorHeading extends DdsElement {
  @property() variant!: HeadingVariant
  @property() label!: string

  static readonly styles = [unsafeCSS(styles), unsafeCSS(typographyStyles)]

  createRenderRoot() {
    adoptLightStyles(this)
    return this
  }

  get elementId() {
    return this.id ? this.id : kebabCase(this.label)
  }

  get anchorTitle() {
    return `Link to ${this.label}`
  }

  render() {
    return html`
    <${unsafeStatic(this.variant)}
      id=${this.elementId}
      part="base"
      class=${clsx(anchorClass, { [`typography-${this.variant}`]: this.variant })}
    >
      ${this.label}
      <dap-ds-link
        part="link"
        exportparts="base: link-base, text: link-text"
        class="dds-anchor__link"
        href=${`#${this.elementId}`}
        aria-label=${this.anchorTitle}
        title=${this.anchorTitle}>
        &#8203;
      </dap-ds-link>
    </${unsafeStatic(this.variant)}>
  `
  }
}
